import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  AccessList,
  useDateFns,
  useHasAccess,
  useIntegrations,
  useLanguage,
  useNavigation,
  useProvider,
  useUser,
} from '~/Hooks';
import {
  requestEventGroupList,
  requestNextEventsByEventGroupIds,
  requestSyncedEventGroups,
  selectEventGroupsList,
} from '~/store/eventGroups';
import {
  requestProviderLocationsList,
  requestProviderTrainerList,
  selectActiveTrainerList,
  selectLocationsMap,
  selectTrainersMap,
} from '~/store/provider';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { Route } from '~/Constants/Routes.constants';
import { useEventGroupColumns } from '~/Screens/EventGroupList/hooks/useEventGroupColumns';
import { selectLoading } from '~/store/app';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { IFitoList } from '~/Components/FitoList/FitoListUncontrolled/interfaces';
import { TableDefinition } from '~/Components/DataTable/DataTable.types';
import { ExternalPartyID } from '~/Models/DTO/externalParties.model';
import { SegmentName } from '~/Constants/Segment.constants';
import { Segment } from '~/Helpers/Segment.helper';
import Yogis from '~/Assets/Icons/brand/Yogis.svg';
import { EventGroupsTab } from '~/Screens/EventGroupList/types';
import { UserRole } from '~/Models/DTO/Role.model';

const calculateActiveTab = ({ active }: { active: boolean | null }): EventGroupsTab => {
  if (active) {
    return EventGroupsTab.ACTIVE;
  }
  if (active === null) {
    return EventGroupsTab.ALL;
  }

  return EventGroupsTab.INACTIVE;
};

export const useEventGroup = () => {
  const { t } = useLanguage();
  const dispatch = useDispatch();
  const { hasAccess } = useHasAccess();
  const { navigate } = useNavigation();
  const { currentUser, role } = useUser();
  const { columnMap } = useEventGroupColumns();
  const { convertToProviderTimezone } = useDateFns();
  const { currentProvider } = useProvider();
  const theme = useTheme();
  const hideInvisibleTabs = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasIntegration } = useIntegrations();
  const isUSCIntegrationEnabled = useMemo(() => hasIntegration(ExternalPartyID.USC_INTEGRATION), [
    hasIntegration,
  ]);
  const [selectedTab, setSelectedTab] = useState<EventGroupsTab>(EventGroupsTab.ACTIVE);
  const showNextEventColumn = selectedTab !== EventGroupsTab.INACTIVE;

  const trainersMap = useSelector(selectTrainersMap);
  const trainers = useSelector(selectActiveTrainerList);
  const locationsMap = useSelector(selectLocationsMap);
  const isForcedEventGroupUSCSync = hasAccess(AccessList.FORCE_EVENT_GROUP_USC_SYNC);
  const isEventGroupsLoading = useSelector(selectLoading(REQUEST_ACTIONS.GET_EVENTGROUP_LIST));
  const eventGroups = useSelector(selectEventGroupsList);

  const getNextEvents = bindActionCreators(requestNextEventsByEventGroupIds, dispatch);
  const getSyncedEventGroups = bindActionCreators(requestSyncedEventGroups, dispatch);
  const getLocations = bindActionCreators(requestProviderLocationsList, dispatch);
  const getTrainers = bindActionCreators(requestProviderTrainerList, dispatch);

  const createEventGroup = useCallback(() => {
    Segment.track(SegmentName.CREATE_EVENT_FROM_EVENT_GROUPS_PAGE);
    navigate(Route.EVENT_CREATE);
  }, [navigate]);
  const placeholder = useMemo(
    () => ({
      imgSrc: Yogis,
      imgAlt: 'No events',
      title: t('app.noResultsFound'),
    }),
    [],
  );
  const tabs = useMemo(
    () => [
      {
        name: t('app.eventGroups.tab.active'),
        query: { active: true },
      },
      {
        name: t('app.eventGroups.tab.inactive'),
        query: { active: false },
      },
      ...(hideInvisibleTabs
        ? []
        : [{ name: t('app.eventGroups.tab.all'), query: { active: null } }]),
    ],
    [t, hideInvisibleTabs],
  );

  const setItemValues = useCallback(
    (item: EventGroup, onClick?: Function): IFitoList => ({
      header: item.name,
      subheader: [
        item.categoryType,
        ...item.trainerIds.map((id) => trainersMap[id]?.name),
        locationsMap[item.locationId]?.name,
      ].join(' ･ '),
      onClick: () => onClick?.(item),
    }),
    [locationsMap, trainersMap],
  );

  const tableDefinition: TableDefinition = useMemo(
    () => ({
      columns: [
        columnMap.name,
        columnMap.providerCategory,
        ...(showNextEventColumn ? [columnMap.nextEvent] : []),
        columnMap.trainer,
        columnMap.location,
        ...(isForcedEventGroupUSCSync || !isUSCIntegrationEnabled
          ? []
          : [columnMap.uscIntegration]),
      ],
      config: { useSelectableRows: true },
    }),
    [columnMap, isForcedEventGroupUSCSync, isUSCIntegrationEnabled],
  );

  const trainerIds = useMemo(
    () =>
      role === UserRole.TRAINER
        ? trainers
            .filter((trainer) => trainer.userId === currentUser?.uuid)
            ?.map((trainer) => trainer.trainerId)
        : undefined,
    [currentUser, role, trainers],
  );

  const listActions = useMemo(
    () => ({
      load: (query: any) => {
        setSelectedTab(calculateActiveTab(query));
        return requestEventGroupList({
          ...query,
          page: query.page - 1,
          size: query.pageSize,
          pageSize: null,
          providerId: currentProvider?.uuid || '',
        });
      },
      actionName: REQUEST_ACTIONS.GET_EVENTGROUP_LIST,
      defaultQuery: {
        archived: false,
        active: true,
        providerId: currentProvider?.uuid || '',
        trainerIds,
      },
    }),
    [currentProvider, setSelectedTab, trainerIds],
  );

  const keyOfId: keyof EventGroup = 'id';
  const settings = useMemo(() => ({ pagination: true }), []);

  const sortOptions = [
    {
      key: 'StartDate',
      label: 'app.sort.dateasc',
    },
    {
      key: 'StartDateDesc',
      label: 'app.sort.datedesc',
    },
    {
      key: 'Name',
      label: 'app.sort.nameAtoZ',
    },
    {
      key: 'NameDesc',
      label: 'app.sort.nameZtoA',
    },
  ];

  useEffect(() => {
    if (currentProvider?.uuid) {
      getTrainers(currentProvider.uuid);
      getLocations(currentProvider.uuid);
    }
  }, [currentProvider]);

  useEffect(() => {
    if (currentProvider?.uuid && !isForcedEventGroupUSCSync) {
      getSyncedEventGroups({ providerId: currentProvider.uuid });
    }
  }, [currentProvider, eventGroups, isForcedEventGroupUSCSync]);

  useEffect(() => {
    if (currentProvider?.uuid && eventGroups.length && showNextEventColumn) {
      getNextEvents({
        providerId: currentProvider.uuid,
        eventGroupIds: eventGroups.map(({ id }) => id),
        from: convertToProviderTimezone(new Date().toString()),
      });
    }
  }, [currentProvider, eventGroups]);

  useEffect(() => {
    if (!selectedTab) {
      return;
    }
    Segment.track(SegmentName.EVENT_GROUPS_TAB, { tab: selectedTab });
  }, [selectedTab]);

  return {
    tabs,
    keyOfId,
    settings,
    selectedTab,
    sortOptions,
    listActions,
    placeholder,
    setItemValues,
    tableDefinition,
    createEventGroup,
    isEventGroupsLoading,
    listReduxSelectorString: selectEventGroupsList,
  };
};
